import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { getTestIds } from '../../utils/TestIds';
import { useBiAction } from '../BiTracking';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';
import { WithInfoIcon } from '../WithInfoIcon/WithInfoIcon';
import styles from './Checkbox.module.scss';
import { CheckedIcon } from './CheckedIcon';
import { PartiallyCheckedIcon } from './PartiallyCheckedIcon';
export const GetCheckboxLabelTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            CheckboxLabelTID: {
                Input: parentTestId ? "".concat(parentTestId, "-input") : undefined,
                Icon: parentTestId ? "".concat(parentTestId, "-icon") : undefined
            }
        }
    });
export const Checkbox = /*#__PURE__*/ React.forwardRef(function Checkbox(props, ref) {
    const { checked, partiallyChecked, disabled, id, className, onChange, children, error, name, value, inline, tooltip, label, labelInfo, testId, bi } = props, rest = _object_without_properties(props, [
        "checked",
        "partiallyChecked",
        "disabled",
        "id",
        "className",
        "onChange",
        "children",
        "error",
        "name",
        "value",
        "inline",
        "tooltip",
        "label",
        "labelInfo",
        "testId",
        "bi"
    ]);
    const { CheckboxLabelTID } = useMemo(()=>GetCheckboxLabelTID(testId), [
        testId
    ]);
    const [isChecked, setIsChecked] = useState(checked || false);
    const logBiAction = useBiAction();
    const isControlled = typeof checked !== 'undefined';
    // Makes sure the internal state reflects the provided checked prop.
    useEffect(()=>{
        if (typeof checked !== 'undefined') {
            setIsChecked(checked);
        }
    }, [
        checked
    ]);
    const LabelWithInfoIcon = ()=>/*#__PURE__*/ _jsx(WithInfoIcon, {
            info: labelInfo || '',
            showInfo: !!labelInfo,
            infoTestId: CheckboxLabelTID.LabelInfo(),
            children: /*#__PURE__*/ _jsx(Typography, {
                skin: disabled ? 'disabled' : 'standard',
                testId: CheckboxLabelTID.Label(),
                children: label
            })
        });
    const checkbox = /*#__PURE__*/ _jsxs("label", _object_spread_props(_object_spread({}, rest, testId != null ? {
        'data-testid': testId
    } : {}), {
        className: cn(styles.root, {
            [styles.disabled]: disabled,
            [styles.inline]: inline
        }, className),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.checkbox,
                children: [
                    /*#__PURE__*/ _jsx("input", {
                        id: id,
                        className: styles.input,
                        ref: ref,
                        type: "checkbox",
                        disabled: disabled,
                        checked: isChecked,
                        "data-testid": CheckboxLabelTID.Input(),
                        onChange: (e)=>{
                            if (!isControlled) {
                                setIsChecked(e.currentTarget.checked);
                            }
                            if (bi === null || bi === void 0 ? void 0 : bi.name) {
                                logBiAction({
                                    entityType: 'checkbox',
                                    entityName: bi.name,
                                    actionType: e.currentTarget.checked ? 'on' : 'off',
                                    data: _object_spread_props(_object_spread({}, bi.data), {
                                        test_id: testId !== null && testId !== void 0 ? testId : ''
                                    })
                                });
                            }
                            onChange && onChange(e);
                        },
                        "aria-invalid": error,
                        name: name,
                        value: value,
                        "data-state": partiallyChecked ? 'partially-checked' : isChecked ? 'checked' : 'unchecked'
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": CheckboxLabelTID.Icon(),
                        className: cn(styles.checkboxIndicator, {
                            [styles.checked]: isChecked && !partiallyChecked,
                            [styles.unchecked]: !isChecked && !partiallyChecked,
                            [styles.partiallyChecked]: partiallyChecked,
                            [styles.error]: error
                        }),
                        children: [
                            isChecked && !partiallyChecked && /*#__PURE__*/ _jsx(CheckedIcon, {}),
                            partiallyChecked && /*#__PURE__*/ _jsx(PartiallyCheckedIcon, {})
                        ]
                    })
                ]
            }),
            children ? children : /*#__PURE__*/ _jsx(LabelWithInfoIcon, {})
        ]
    }));
    return tooltip ? /*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({}, tooltip), {
        children: /*#__PURE__*/ _jsx("div", {
            children: checkbox
        })
    })) : checkbox;
});
